
import { useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Notification } from '@/types/notifications';
import { useAuth } from '@/contexts/auth';
import { toast } from 'sonner';

// Define a type for the profile data returned from the join query
interface SenderProfile {
  id: string;
  username: string | null;
  full_name: string | null;
  avatar_url: string | null;
}

export const useNotificationsFetcher = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // DEEP PURGE: Completely remove all notifications and create a fresh welcome
  const deepPurgeNotifications = useCallback(async () => {
    if (!user?.id) return false;
    
    console.log('DEEP PURGE: Attempting to reset notifications for user:', user.id);
    
    try {
      setLoading(true);
      
      // Use type assertion to bypass TypeScript type checking for RPC function name
      const { data, error } = await supabase.rpc(
        'invoke_reset_notifications' as any, 
        {
          p_user_id: user.id,
          p_welcome_message: 'Welcome to ChapterSeed! Your notifications feed has been reset.'
        }
      );
      
      if (error) {
        console.error('Error in reset_notifications RPC:', error);
        throw new Error(`Failed to reset notifications: ${error.message}`);
      }
      
      console.log('Reset notifications response:', data);
      
      // Update local notifications state with the welcome notification
      if (data && Array.isArray(data) && data.length > 0) {
        const welcomeNotifications = data.map(notification => ({
          ...notification,
          sender_name: 'System Notification',
          sender_avatar: null
        }));
        
        setNotifications(welcomeNotifications);
        console.log('DEEP PURGE: Successfully reset notifications, new count:', welcomeNotifications.length);
      } else {
        // No notifications were returned, but operation was successful
        setNotifications([]);
        console.log('DEEP PURGE: Successfully reset notifications, but no welcome notification returned');
      }
      
      return true;
    } catch (error) {
      console.error('Error during deep purge:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Function to purge incorrect notifications for the current user
  const purgeIncorrectNotificationsForCurrentUser = useCallback(async () => {
    if (!user?.id) return false;
    
    console.log('EMERGENCY PURGE: Attempting to purge incorrect notifications for user:', user.id);
    
    try {
      setLoading(true);
      
      // Delete potentially incorrect notifications
      const { error } = await supabase
        .from('notifications')
        .delete()
        .eq('recipient_id', user.id)
        .neq('type', 'system');
          
      if (error) {
        console.warn('Error during emergency purge:', error.message);
        return false;
      }
      
      // Create a fresh notification
      const { error: insertError } = await supabase
        .from('notifications')
        .insert({
          recipient_id: user.id,
          sender_id: null,
          type: 'system',
          content: 'Your notification feed has been refreshed. You will now only see notifications relevant to you.',
          is_read: false
        });
        
      if (insertError) {
        console.error('Error creating system notification:', insertError);
        return false;
      }
      
      // Refresh notifications
      await fetchNotifications();
      
      return true;
    } catch (error) {
      console.error('Error during emergency purge:', error);
      return false;
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // Function to create a welcome notification if none exist
  const createWelcomeNotificationIfNeeded = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      // Only create welcome notification if there are no notifications
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('recipient_id', user.id)
        .limit(1);
        
      if (error) {
        console.warn('Error checking for existing notifications:', error);
        return;
      }
      
      // If user has no notifications, create welcome notification
      if (!data || data.length === 0) {
        console.log('Creating welcome notification for user:', user.id);

        // Create welcome notification from system (null sender)
        const { error: insertError } = await supabase
          .from('notifications')
          .insert({
            recipient_id: user.id,
            sender_id: null,
            type: 'system',
            content: 'Welcome to ChapterSeed! Start exploring stories from your favorite authors.',
            is_read: false
          });
          
        if (insertError) {
          console.error('Error creating welcome notification:', insertError);
        } else {
          console.log('Created welcome notification successfully');
        }
      }
    } catch (error) {
      console.error('Error in welcome notification flow:', error);
    }
  }, [user?.id]);

  // Function to fetch notifications using Supabase
  const fetchNotifications = useCallback(async () => {
    if (!user?.id) {
      setNotifications([]);
      setLoading(false);
      return [];
    }

    try {
      setLoading(true);
      
      // First ensure welcome notification exists
      await createWelcomeNotificationIfNeeded();
      
      console.log('Fetching notifications for user ID:', user.id);
      
      // Use direct join query with explicit ordering
      const { data, error } = await supabase
        .from('notifications')
        .select(`
          *,
          sender:profiles!notifications_sender_id_fkey(id, username, full_name, avatar_url)
        `)
        .eq('recipient_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);
          
      if (error) {
        console.error('Error fetching notifications:', error);
        throw error;
      }
      
      // Debug log to see what we're getting back
      console.log('Raw notification data:', data);
      
      // Transform the data with proper sender information
      const formattedNotifications = data.map((notification) => {
        // Handle direct query format
        const rawSender = notification.sender;
        let senderName = 'System Notification';
        let senderAvatar = null;
        
        // Safely check if sender exists and is valid
        if (rawSender && typeof rawSender === 'object' && rawSender !== null) {
          // Check if it's not an error object by checking if the 'error' property exists
          // First cast rawSender to Record<string, unknown> to ensure type safety
          const senderRecord = rawSender as Record<string, unknown>;
          
          if (!('error' in senderRecord)) {
            const senderProfile = rawSender as SenderProfile;
            senderName = senderProfile?.full_name || senderProfile?.username || 'System Notification';
            senderAvatar = senderProfile?.avatar_url;
          }
        }
        
        return {
          ...notification,
          sender_name: senderName,
          sender_avatar: senderAvatar
        };
      });
      
      console.log('Fetched and formatted notifications:', formattedNotifications.length);
      
      // Explicitly sort by created_at in descending order (newest first)
      formattedNotifications.sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      
      setNotifications(formattedNotifications);
      return formattedNotifications;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user?.id, createWelcomeNotificationIfNeeded]);

  // Fetch notifications on user change
  useEffect(() => {
    if (user?.id) {
      console.log('User ID changed, fetching notifications for:', user.id);
      fetchNotifications().catch(err => {
        console.error('Failed to fetch initial notifications:', err);
      });
    } else {
      setNotifications([]);
    }
  }, [user?.id, fetchNotifications]);

  return {
    notifications,
    loading,
    fetchNotifications,
    setNotifications,
    purgeIncorrectNotificationsForCurrentUser,
    deepPurgeNotifications
  };
};
